import { Language, Provider, WorkflowCodeType, WorkflowComponentParamType, WorkflowComponentParameterMode, WorkflowComponentValueType, WorkflowParameterValueErrorType } from '../../types';
import { isExpressionValue } from '../../utils';
export class WorkflowComponent {
    constructor(metadata, inputs, outputs) {
        this.hidden = false;
        this.supportedProviders = [
            Provider.BigQuery,
            Provider.Postgres,
            Provider.Redshift,
            Provider.Snowflake,
            Provider.DatabricksRest
        ];
        this.requiresDynamicSQL = false;
        this.metadata = metadata;
        this.metadata.tags = metadata.tags ?? [];
        this.metadata.starred = metadata.starred ?? false;
        this.inputs = inputs.map((v) => {
            if (!v.description) {
                v.description = v.title;
            }
            return v;
        });
        this.outputs = outputs;
    }
    static clone(component) {
        return Object.assign(Object.create(Object.getPrototypeOf(component), Object.getOwnPropertyDescriptors(component)), JSON.parse(JSON.stringify(component)));
    }
    getVersion(provider) {
        return '1';
    }
    /**
     * Returns the language used by the component code in the given provider
     * @param provider The type of provider where the component will be executed
     * @returns the language used by the component code in the given provider
     */
    language(provider) {
        return Language.SQL;
    }
    /**
     * Checks if the passed parameters are correct and enough for running the component.
     * If parameters are missing or any of them has an incorrect value, an error is added
     * to the array returned.
     */
    checkInputValues(params) {
        const errors = [];
        for (const input of this.inputs) {
            this._checkInputValueIsNotMissing(params, input, errors);
            this._checkInputValueTypeAndRange(params, input, errors);
            //this._checkColumnExists(params, input, errors)
            this._checkVariableUsage(params, input, params.variables || [], errors);
        }
        return errors;
    }
    _checkColumnExists(params, input, errors) {
        const schemas = params.schemas;
        if (schemas) {
            const flatSchemas = schemas.flatMap((x) => x.schemas);
            const tableSchema = flatSchemas.find((x) => x.fqn ===
                params.resolvedParamValues[input.parent])?.schema;
            if (tableSchema) {
                const columns = Array.isArray(params.resolvedParamValues[input.name])
                    ? params.resolvedParamValues[input.name]
                    : [params.resolvedParamValues[input.name]];
                for (const column of columns) {
                    if (!tableSchema.find((x) => x.name === column)) {
                        errors.push({
                            parameter: 'column',
                            type: WorkflowParameterValueErrorType.WrongValue,
                            message: `Column ${column} not found in table`
                        });
                    }
                }
            }
        }
    }
    _checkVariableUsage(params, input, variables, errors) {
        const validVariables = variables.map((v) => v.name);
        const usedVariables = [];
        const param = params.params[input.name];
        if (param != null &&
            !Array.isArray(param) &&
            isExpressionValue(param) &&
            input.allowExpressions !== false) {
            const stringValue = `${param.value}`;
            const matches = stringValue.match(/@([a-zA-Z_$][a-zA-Z_$0-9]*)/g);
            if (matches) {
                for (const match of matches) {
                    const variable = match.substring(1);
                    if (!usedVariables.includes(variable)) {
                        usedVariables.push(variable);
                        if (!validVariables.includes(variable)) {
                            errors.push({
                                message: `Undefined variable @${variable}`,
                                type: WorkflowParameterValueErrorType.WrongValue,
                                parameter: input.name
                            });
                        }
                    }
                }
            }
        }
    }
    _checkInputValueIsNotMissing(params, input, errors) {
        const isHidden = !this._filterShowIf(input, params.resolvedParamValues);
        const parent = input.parent;
        const unconfiguredParent = parent && params.resolvedParamValues?.[parent] == null;
        const optional = (input.optional ?? false) ||
            input.default !== undefined ||
            unconfiguredParent ||
            isHidden;
        const param = params.params[input.name];
        const resolvedParam = params.resolvedParamValues?.[input.name];
        if (resolvedParam == null) {
            if (!optional) {
                errors.push({
                    message: `No value assigned`,
                    type: WorkflowParameterValueErrorType.MissingValue,
                    parameter: input.name
                });
            }
        }
        else {
            if (!Array.isArray(param)) {
                if (param.type === WorkflowComponentValueType.Literal) {
                    const value = param.value;
                    if (!isExpressionValue(param)) {
                        if (value == null ||
                            ([
                                WorkflowComponentParamType.String,
                                WorkflowComponentParamType.StringSql
                            ].includes(input.type) &&
                                value.trim() === '')) {
                            if (!optional) {
                                errors.push({
                                    message: `No value assigned`,
                                    type: WorkflowParameterValueErrorType.MissingValue,
                                    parameter: input.name
                                });
                            }
                        }
                    }
                }
            }
            else {
                if (resolvedParam.length === 0 && !optional) {
                    errors.push({
                        message: `No value assigned`,
                        type: WorkflowParameterValueErrorType.MissingValue,
                        parameter: input.name
                    });
                }
            }
        }
    }
    _checkInputValueTypeAndRange(params, input, errors) {
        const param = params.params[input.name];
        if (param != null) {
            if (!Array.isArray(param)) {
                if (param.type === WorkflowComponentValueType.Literal) {
                    const value = param.value;
                    if (value) {
                        if (!isExpressionValue(param)) {
                            if (input.type === WorkflowComponentParamType.Number) {
                                const floatValue = parseFloat(value);
                                if (!Number.isFinite(floatValue)) {
                                    errors.push({
                                        message: `Invalid numeric value`,
                                        type: WorkflowParameterValueErrorType.WrongValue,
                                        parameter: input.name
                                    });
                                }
                                const min = input.min;
                                if (min !== undefined && floatValue < min) {
                                    errors.push({
                                        message: `Value lower than the minimum`,
                                        type: WorkflowParameterValueErrorType.WrongValue,
                                        parameter: input.name
                                    });
                                }
                                const max = input.max;
                                if (max !== undefined && floatValue > max) {
                                    errors.push({
                                        message: `Value larger than the maximum`,
                                        type: WorkflowParameterValueErrorType.WrongValue,
                                        parameter: input.name
                                    });
                                }
                            }
                            else if (input.type === WorkflowComponentParamType.Range) {
                                const range = JSON.parse(value);
                                if (range.length !== 2) {
                                    errors.push({
                                        parameter: input.name,
                                        type: WorkflowParameterValueErrorType.WrongValue,
                                        message: 'The return range must contain two values'
                                    });
                                }
                                const minValue = parseFloat(range[0]);
                                if (!Number.isFinite(minValue)) {
                                    errors.push({
                                        message: `Invalid numeric value ${range[0]}`,
                                        type: WorkflowParameterValueErrorType.WrongValue,
                                        parameter: input.name
                                    });
                                }
                                const maxValue = parseFloat(range[1]);
                                if (!Number.isFinite(maxValue)) {
                                    errors.push({
                                        message: `Invalid numeric value ${range[1]}`,
                                        type: WorkflowParameterValueErrorType.WrongValue,
                                        parameter: input.name
                                    });
                                }
                                if (minValue > maxValue) {
                                    errors.push({
                                        message: `The minimum value must be less than the maximum value`,
                                        type: WorkflowParameterValueErrorType.WrongValue,
                                        parameter: input.name
                                    });
                                }
                            }
                            else if (input.type === WorkflowComponentParamType.String &&
                                input.validation &&
                                !RegExp(input.validation).test(value)) {
                                errors.push({
                                    message: `Invalid string value`,
                                    type: WorkflowParameterValueErrorType.WrongValue,
                                    parameter: input.name
                                });
                            }
                        }
                        else if (input.allowExpressions !== undefined &&
                            !input.allowExpressions) {
                            errors.push({
                                message: `Expressions not allowed for this parameter`,
                                type: WorkflowParameterValueErrorType.WrongValue,
                                parameter: input.name
                            });
                        }
                    }
                }
            }
        }
    }
    _filterShowIf(input, params) {
        const { showIf } = input;
        if (!showIf)
            return true;
        return showIf.every(({ parameter: inputName, value, invert }) => {
            const dependentInput = this.inputs.find((input) => input.name === inputName);
            if (dependentInput) {
                // If the dependent input is hidden, its condition is not relevant
                if (!this._filterShowIf(dependentInput, params)) {
                    return true;
                }
                // Check the condition for single or multiple values
                const dependentInputValue = params[inputName] ?? dependentInput?.default;
                const condition = Array.isArray(value)
                    ? value.includes(dependentInputValue)
                    : value === dependentInputValue;
                return invert ? !condition : condition;
            }
        });
    }
    /**
     * Ensures that if there are showIf condition in the input params, those params
     * that do not meet this conditions are set to null.
     * @param params the parameters, as they are set in a workflow for executing this component.
     * @returns
     */
    _removeHiddenParams(params) {
        const preparedParams = { ...params };
        let paramUpdated = true;
        while (paramUpdated) {
            paramUpdated = false;
            for (const input of this.inputs) {
                if (!this._filterShowIf(input, preparedParams)) {
                    const paramNewValue = undefined;
                    if (preparedParams[input.name] !== paramNewValue) {
                        preparedParams[input.name] = paramNewValue;
                        paramUpdated = true;
                    }
                }
            }
        }
        return preparedParams;
    }
    /**
     * Prepares parameter values for the component execution.
     * This method is to be called from the getCode method, to ensure that parameter values are correct.
     * It will, for instance, replace null values (not set in the workflow for this component)
     * with the default value of the component input, if any.
     * @param params the parameters, as they are set in a workflow for executing this component.
     * @returns
     */
    _prepareParameters(params) {
        const preparedParams = {};
        for (const input of this.inputs) {
            const multiple = input?.mode === WorkflowComponentParameterMode.Multiple;
            let value = params[input.name] == null || params[input.name] === ''
                ? input.default ?? (multiple ? [] : null)
                : params[input.name];
            if (!Array.isArray(value)) {
                value = [value];
            }
            value = value.filter((x) => x != null);
            if (!multiple) {
                preparedParams[input.name] = value.length ? value[0] : null;
            }
            else {
                preparedParams[input.name] = value;
            }
        }
        return this._removeHiddenParams({ ...params, ...preparedParams });
    }
    /**
     * Returns the code of the component including all the additional logic required to be part of a workflow script
     * @param workflowComponentCodeParams The parameters used to run the component
     * @returns The full code and outputs corresponding to this component as part of a given workflow.
     * Null if the component cannot be used in the provided connection
     */
    async getFullCodeSnippet(workflowComponentCodeParams) {
        const { workflow, connection, componentName, codeType } = workflowComponentCodeParams;
        const codeReturn = await this.getCode({
            ...workflowComponentCodeParams,
            codeType: codeType
        });
        const language = this.language(connection.type);
        if (codeReturn?.query) {
            let query = '';
            if (codeType === WorkflowCodeType.StoredProcedure) {
                query = connection.wrapComponentCodeInFlowControlBlock(codeReturn.query, codeType, language);
            }
            else {
                if ([WorkflowCodeType.Full, WorkflowCodeType.FullNoCache].includes(codeType)) {
                    query = connection.getComponentDescriptionAsComment(workflow, componentName);
                }
                query += connection.wrapComponentCodeInFlowControlBlock(codeReturn.query, codeType, language);
            }
            codeReturn.query = query;
        }
        return codeReturn;
    }
    /**
     * Returns the output tables of this component
     * @param workflow The workflow that is executing this component,
     * @param params The parameters used to run the component
     * @param connection The connection where the component is to be run
     * @param codeType The type of code
     * @returns The output tables of this component as an object.
     * Property names are output names, property values are table FQNs.
     */
    getOutputTables({ workflow, params, connection, codeType }) {
        const tables = {};
        for (const output of this.outputs) {
            tables[output.name] = connection.getOutputTableName(workflow.id, this.metadata.name, params, output.name, codeType);
        }
        return tables;
    }
    getRequiredGrants({}) {
        return {
            grants: [],
            apis: []
        };
    }
    /**
     * Return values needed to customize the UI element used by this component
     * @returns An object with the properties to be used to create the custom UI element
     */
    getUIProperties(provider) {
        return {};
    }
    /**
     * Return the name of the UI class to be used for allowing the user to configure this component
     * @returns The name of the UI class to use for this component
     */
    getUIClass() {
        return 'default';
    }
    supportsProvider(provider) {
        return this.supportedProviders.includes(provider);
    }
    /**
     * Return the version of the analytics toolbox required to run this component
     * @param provider The provider where the component is to be run
     * @returns The version of the analytics toolbox required to run this component
     * Null if the component does not require the Analytics Toolbox
     */
    requiredAnalyticsToolboxVersion(provider) {
        return null;
    }
    /**
     * Modifies the component to adapt it to the provider and environment variables of the workflow.
     * This allows to have more flexible parameter definitions.
     * @param provider The provider where the component is to be run
     * @param envVars The environment variables of the workflow
     */
    adaptComponent(provider, envVars) {
        // do nothing on the default implementation
    }
    /**
     * Returns true if the component can provide the schemas of its outputs without
     * running the component in the data warehouse
     * @returns True if the component can provide the schemas of its outputs
     */
    canProvideOutputSchemas(params) {
        return false;
    }
    /**
     * Returns the schemas of the outputs of this component
     * @param workflow The workflow that is executing this component,
     * @param connection The connection where the component is to be run
     * @param params The parameters used to run the component
     * @param schemas The schemas of the inputs of this component
     * @param envVars The environment variables of the workflow
     * @param componentName The name of the component in the workflow
     * @returns The schemas of the outputs of this component
     */
    getOutputSchemas({ workflow, connection, params, schemas, envVars, componentName }) {
        let outputSchemas = this._getOutputSchemas({
            workflow,
            connection,
            params,
            schemas,
            envVars,
            componentName
        });
        outputSchemas = outputSchemas.map((schema) => {
            if (!schema.schema) {
                return schema;
            }
            return {
                fqn: schema.fqn,
                schema: schema.schema.map((column) => {
                    return {
                        name: connection.applyCapitalization(column.name),
                        type: column.type,
                        db_type: column.db_type
                    };
                })
            };
        });
        return outputSchemas;
    }
    _getOutputSchemas({ workflow, connection, params, schemas, envVars, componentName }) {
        return [];
    }
    /**
     * Returns the queries to get the schemas of the outputs of this component
     * @param workflow The workflow that is executing this component,
     * @param connection The connection where the component is to be run
     * @param params The parameters used to run the component
     * @param schemas The schemas of the inputs of this component
     * @returns The queries to get the schemas of the outputs of this component.
     * Null if the component cannot provide the schemas of its outputs
     * (mainly because of being wrongly configured)
     * Queries must be executed in the provided order.
     * The response from the last query will contain the schemas of the outputs
     */
    async getOutputSchemasQueries({ workflow, connection, params, schemas, envVars, componentName }) {
        if (!schemas) {
            return null;
        }
        const statements = [];
        // create empty input tables following the passed input schemas
        for (const input of this.inputs) {
            if (input.type === WorkflowComponentParamType.InputTable) {
                const value = params[input.name];
                if (value) {
                    let fqnArray;
                    if (Array.isArray(value)) {
                        fqnArray = value;
                    }
                    else {
                        fqnArray = [value];
                    }
                    for (const fqn of fqnArray) {
                        const tableSchema = this._getSchemaForTablename(fqn, schemas, connection);
                        if (tableSchema) {
                            let createTableSQL = connection.createTableFromSchema(tableSchema, fqn);
                            statements.push(createTableSQL);
                        }
                    }
                }
            }
        }
        // add component code
        const code = await this.getCode({
            workflow,
            connection,
            params,
            codeType: WorkflowCodeType.DryRun,
            envVars,
            componentName
        });
        if (code?.query) {
            let query = connection.replaceVariablesInScriptQuery(code.query, workflow.variables);
            query = connection.wrapComponentCodeInFlowControlBlock(query, WorkflowCodeType.DryRun, this.language(connection.type));
            statements.push(query);
        }
        else {
            return null;
        }
        // add queries to get the schemas of the outputs
        const outputTables = this.getOutputTables({
            workflow,
            params,
            connection,
            codeType: WorkflowCodeType.DryRun,
            componentName
        });
        const schemaQuery = connection.getOutputSchemasQueries(Object.values(outputTables).map((fqn) => {
            return fqn.split('.').pop();
        }));
        statements.push(schemaQuery);
        return connection.prepareSchemaQueries(statements);
    }
    _getDryRunOutputTableFqn(workflow, params, connection, componentName, outputName) {
        const outputTables = this.getOutputTables({
            workflow,
            params,
            connection,
            codeType: WorkflowCodeType.DryRun,
            componentName
        });
        return outputTables[outputName];
    }
    _getSchemaForTablename(tablename, schemas, connection) {
        schemas = schemas.map((s) => {
            return Object.keys(s).reduce((acc, k) => {
                acc[k.toLowerCase()] = s[k];
                return acc;
            }, {});
        });
        const schema = schemas.find((s) => connection.applyCapitalization(s.fqn) ===
            connection.applyCapitalization(tablename));
        return schema?.schema;
    }
    /**
     * Returns true if the output table for a given output is generated with the passed configuration
     * @param workflow The workflow that is executing this component,
     * @param componentName The name of the component
     * @param outputName The name of the output to be checked
     * @param params The parameters used to run the component
     */
    isOutputTableCreated(workflow, componentName, outputName, params) {
        return true;
    }
}
